import {
  Component,
  h,
  Prop,
  Watch,
  Element,
  Method,
  Event,
  EventEmitter,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';
import { responsive } from '../../global/utility/responsive/responsive';
import { isMagnoliaEditingMode } from '../../helper';
import { Link } from '../ui-link/ui-link';
import { Icon } from '../ui-icon/ui-icon';

// TODO: Use slots for content
@Component({
  tag: 'ui-teaser',
  styleUrl: 'ui-teaser.scss',
  shadow: true,
})
export class UiTeaser {
  link?: HTMLUiLinkElement;

  constructor() {
    this.onTeaserClick = this.onTeaserClick.bind(this);
  }

  @Element() el: HTMLElement;

  @Prop() headline: string;

  @Prop() text: string;

  @Prop() date: string;

  @Prop() category: string;

  @Prop() author: string;

  @Prop() image: string;

  /**
   * Provides the original asset ratio to be considered in the responsive image URL.
   * See ui-image component for more information.
   */
  @Prop() imageRatio?: string;

  @Prop() icon: Icon;

  @Prop({ attribute: 'image-alt-text' }) imageAltText: string;

  @Prop({ attribute: 'image-mouseover' }) imageMouseover: string;

  @Prop() copyright: string;

  @Prop() alignButton = false;

  @Prop({ attribute: 'media-selection' }) mediaSelection:
    | 'none'
    | 'image'
    | 'video'
    | 'icon';

  @Prop({ attribute: 'video-src' }) videoSrc: string;

  @Prop({ attribute: 'video-loop-setting' }) videoLoopSetting?: string;

  @Prop() replace?: string;

  @Prop() sizes?: string;

  @Method()
  async hasLinks() {
    return this.links?.length > 0;
  }

  @Prop({ mutable: true }) links!: Link[];

  @Prop({ attribute: 'links' }) utilityLinks: string;

  @Watch('utilityLinks')
  utilityLinksChangeHandler(newValue: string) {
    this.links = newValue ? JSON.parse(newValue) : this.links;
  }

  @Event({ eventName: 'componentResized' }) componentResized: EventEmitter;

  @Event({ eventName: 'linkClick' }) linkClick: EventEmitter<{ link: Link }>;

  @Method()
  async onAfterResize() {
    this.componentResized.emit();
  }

  componentWillLoad() {
    this.utilityLinksChangeHandler(this.utilityLinks);
  }

  componentDidLoad() {
    responsive(this.el);
  }

  @Prop() variant:
    | 'primary'
    | 'primary100'
    | 'secondary100'
    | 'white'
    | 'border'
    | 'transparent-overlay'
    | 'transparent-no-indentation' = 'secondary100';

  @Prop({ attribute: 'is-text-floating' }) isTextFloating: boolean;

  onTeaserClick(e: Event) {
    if (!isMagnoliaEditingMode() && e.target !== this.link) {
      this.link.triggerClick();
    }
  }

  createLink(link: Link, couldBeButton = true) {
    return (
      <ui-link
        key={link.title}
        ref={(el) => {
          this.link = el || this.link;
        }}
        class={{
          teaser__link: true,
          'teaser__link--btn': couldBeButton && link.style !== 'text',
        }}
        href={link.src}
        button={link.style?.replace('text', '').replace('button-', '') as any}
        icon={link.icon}
        iconOption={link.iconOption}
        target={link.target}
        delay={link.delay}
        onClick={() => {
          this.linkClick.emit({ link });
        }}
      >
        {link.title}
      </ui-link>
    );
  }

  render() {
    return (
      <article
        class={{
          teaser: true,
          'teaser--float-img': this.isTextFloating,
          'teaser--box-white': this.variant === 'white',
          'teaser--box-dark-blue': this.variant === 'primary',
          'teaser--box-light-blue': this.variant === 'primary100',
          'teaser--box-grey': this.variant === 'secondary100',
          'teaser--box-transparent': this.variant === 'transparent-overlay',
          'teaser--box-border teaser--box-white': this.variant === 'border',
          'teaser--box-white no-indentation':
            this.variant === 'transparent-no-indentation',
          'teaser--align-button': this.alignButton,
          'teaser--no-media': this.mediaSelection === 'none',
        }}
        onClick={
          this.links?.length === 1 && this.mediaSelection !== 'video'
            ? this.onTeaserClick.bind(this)
            : null
        }
      >
        <div
          class={
            this.links?.length === 1 && this.mediaSelection !== 'video'
              ? 'is-linked'
              : ''
          }
        >
          {this.mediaSelection !== 'none' && (
            <div class="teaser__figure">
              {this.mediaSelection === 'image' && this.image ? (
                <ui-image
                  class="teaser__image"
                  altText={this.imageAltText}
                  src={this.image}
                  ratio={this.imageRatio}
                  replace={this.replace}
                  sizes={this.sizes}
                ></ui-image>
              ) : null}
              {this.mediaSelection === 'video' && this.videoSrc ? (
                <ui-video
                  video-id={this.videoSrc}
                  looped={this.videoLoopSetting !== 'no-loop'}
                  muted={this.videoLoopSetting === 'loop-muted'}
                >
                  <slot></slot>
                </ui-video>
              ) : null}

              {this.mediaSelection === 'icon' && this.icon ? (
                <ui-icon
                  class="teaser__icon"
                  icon={this.icon.icon}
                  variant={this.icon.variant}
                  type={this.icon.type}
                ></ui-icon>
              ) : null}
            </div>
          )}
          {this.copyright ? (
            <div class="teaser__copyright">© {this.copyright}</div>
          ) : null}
          <div class="teaser__body">
            <ui-headline
              level={3}
              innerHTML={this.headline}
              class="teaser__headline"
            ></ui-headline>
            <p class="teaser__text" innerHTML={this.text}></p>
            {this.date || this.author || this.category ? (
              <ul class="teaser__meta">
                {this.date ? (
                  <li>
                    <time>{this.date}</time>
                  </li>
                ) : null}
                {this.author ? <li>{this.author}</li> : null}
                {this.category ? <li>{this.category}</li> : null}
              </ul>
            ) : null}
            {this.links?.length === 1 ? (
              this.links.map((link, i) =>
                this.alignButton ? (
                  <div class="button-wrapper" key={i}>
                    {this.createLink(link)}
                  </div>
                ) : (
                  this.createLink(link)
                )
              )
            ) : (
              <ul class="teaser__link-list">
                {this.links?.map((link) => (
                  <li
                    key={link.title}
                    class={{
                      'teaser__link-list-item': true,
                      'teaser__link-list-item--btn': link.style !== 'text',
                    }}
                  >
                    {this.createLink(link, false)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </article>
    );
  }
}

versionComponent(UiTeaser);
